import { gql } from '@apollo/client'
import { Maybe } from '@graphql-tools/utils'
import { CONTENT_AVAIL_FRAG } from '@/services/ContentAvailabilityService'
import { WithRequired } from '@/services/types'
import { HeroBannerBuilder, HeroBannerDataFragment } from '@/types/codegen-federation'

export interface HeroBannerProps {
  hero: HeroBannerData
  isGuildMember: boolean
  trackPress: (buttonPress?: string) => void
  onJoinGuildPress: (theaterSlug: string) => void
}

export type HeroInput = Maybe<HeroBannerDataFragment> &
  Maybe<{ recommendationId: Maybe<string>; attribution?: Maybe<unknown> }>

export const isHeroBannerData = (banner: HeroInput): banner is HeroBannerData => {
  return (
    !!banner?.recommendationId &&
    !!banner?.id &&
    !!banner?.type &&
    !!banner?.project?.slug &&
    !!banner?.project?.title &&
    validFirstWatchable(banner)
  )
}

const EXCLUDED_CONTENT_TYPES = new Set(['ContentSeason', 'ContentSeries'])

const validFirstWatchable = (banner: HeroInput) => {
  if (!banner?.project?.title) return false

  if (banner.project.title.__typename === 'ContentSeries' && banner.project.title.firstWatchable?.__typename) {
    return !EXCLUDED_CONTENT_TYPES.has(banner.project.title.firstWatchable.__typename)
  }

  return true
}

type QueriedHeroTitle = NonNullable<NonNullable<HeroBannerDataFragment['project']>['title']>

type QueriedHeroSeries = Extract<QueriedHeroTitle, { __typename: 'ContentSeries' }>
type HeroSeries = QueriedHeroSeries & {
  firstWatchable: Exclude<QueriedHeroSeries['firstWatchable'], { __typename: 'ContentSeason' | 'ContentSeries' }>
}
type HeroMovie = Extract<QueriedHeroTitle, { __typename: 'ContentMovie' }>
type HeroSpecial = Extract<QueriedHeroTitle, { __typename: 'ContentSpecial' }>

export type HeroTitle = HeroSeries | HeroMovie | HeroSpecial

export type HeroBannerData = {
  __typename?: HeroBannerDataFragment['__typename']
  recommendationId: string
  attribution?: unknown
  id: HeroBannerDataFragment['id']
  type: NonNullable<HeroBannerDataFragment['type']>
  flavorText: HeroBannerDataFragment['flavorText']
  project: WithRequired<Omit<NonNullable<HeroBannerDataFragment['project']>, 'title'>, 'slug'> & { title: HeroTitle }
  genericConfig: HeroBannerBuilder
}

export const HERO_BANNER_FRAGMENT = gql`
  ${CONTENT_AVAIL_FRAG}

  fragment HeroBannerData on HeroBanner {
    id
    type
    flavorText
    genericConfig {
      id
      ... on HeroBannerBuilder {
        link
        cta
        cloudinaryPath
        landscapeCloudinaryPath
        logo
        logoCloudinaryPath
      }
      ... on HeroBannerGenericWatchable {
        watchable {
          __typename
          ... on ContentDisplayable {
            id
            ...ContentAvailability
            landscapeBackdrop: image(aspect: "16:9", category: BACKDROP) {
              cloudinaryPath
            }
            portraitBackdrop: image(aspect: "9:16", category: BACKDROP) {
              cloudinaryPath
            }
          }
        }
      }
    }
    project {
      slug
      discoveryPosterLandscapeCloudinaryPath
      discoveryPosterCloudinaryPath
      logoCloudinaryPath
      streamingAtLabel
      releaseDate
      name
      projectType
      highestScore
      franchiseDetails {
        franchiseId
        slug
      }
      title {
        __typename
        ... on ContentDisplayable {
          id
          ...ContentAvailability
          landscapeBackdrop: image(aspect: "16:9", category: BACKDROP) {
            cloudinaryPath
          }
          landscapeNonTitle: image(aspect: "16:9", category: NON_TITLE_ART) {
            cloudinaryPath
          }
          portraitBackdrop: image(aspect: "9:16", category: BACKDROP) {
            cloudinaryPath
          }
          portraitNonTitle: image(aspect: "9:16", category: NON_TITLE_ART) {
            cloudinaryPath
          }
          squareNonTitle: image(aspect: "1:1", category: NON_TITLE_ART) {
            cloudinaryPath
          }
          logo: image(aspect: "9:5", category: LOGO) {
            cloudinaryPath
          }
        }
        ... on ContentSeries {
          id
          genres
          ratings
          firstWatchable {
            __typename
            ... on Node {
              id
            }
            ...ContentAvailability
          }
        }
        ... on ContentMovie {
          id
          genres
          ratings
        }
        ... on ContentSpecial {
          id
          genres
          ratings
        }
      }
    }
  }
`
